import cm from 'classnames';

import { useAppSelector, useAppDispatch } from 'store/store-hooks';
import { useLocation, Path } from 'components/_hooks/use-router';
import { selectHelpMode } from 'components/app/_mediator/app-data.slice';
import { SbTrackState, SbTracksMode, setScrollSyncLock, setView } from 'components/app/storyboard/_mediator/sb.slice';
import { IntroKeys } from 'components/app/intro/intro-keys';

import { ReactComponent as VolumeMute } from 'assets/wavesurfer/volume-mute.svg';
import { ReactComponent as Volume } from 'assets/wavesurfer/volume.svg';
import { ReactComponent as MouseScroll } from 'assets/ui/mouse-scroll.svg';
import { ReactComponent as CollapseIcon } from 'assets/wavesurfer/collapse.svg';
import { ReactComponent as ExpandIcon } from 'assets/wavesurfer/expand.svg';

import { Tooltip } from 'components/UI/tooltip/tooltip';
import { WsButton } from './elements/ws-button';
import { WsHeaderTitle } from './_ws-header-title';
import { WsHeaderMarkers } from './_ws-header-markers';
import { WsHeaderMarkersReset } from './_ws-header-markers-reset';
import styles from './_ws-header.module.scss';

export const WsHeader = ({ ws }: { ws: WaveSurfer }) => {
  const { path } = useLocation();
  const isMute = useAppSelector((state) => state.audio.isMute);
  const sbViewMode = useAppSelector((state) => state.sb.sbViewMode);
  const scrollSyncLock = useAppSelector((state) => state.sb.scrollSyncLock);
  const helpMode = useAppSelector(selectHelpMode);
  const dispatch = useAppDispatch();

  const toggleHeaderState = async () => {
    const isExpanded = !sbViewMode.audio && sbViewMode.prevSbTracks;
    const sbTrackModeState: SbTracksMode = isExpanded
      ? sbViewMode.prevSbTracks!
      : { storyBoard: 'collapsed', clipBoard: 'collapsed', discardBoard: 'collapsed' };
    const view = {
      audio: (isExpanded ? 'collapsed' : null) as SbTrackState,
      sbTracks: sbTrackModeState ?? undefined,
      prevSbTracks: isExpanded ? sbViewMode.prevSbTracks : sbViewMode.sbTracks,
    };
    dispatch(setView(view));
  };

  return (
    <Tooltip
      tip={`Music track: The beat markers define the clip locations and durations`}
      showTooltip={helpMode}
      position="bottom"
    >
      <div data-intro={IntroKeys.sbAudioHeader} className={styles.WsHeader}>
        <WsButton tip="Music track preview volume" tipPosition="right" onClick={() => ws.setMute(!isMute)}>
          {isMute ? <VolumeMute /> : <Volume />}
        </WsButton>
        <Tooltip showTooltip={helpMode} tip="Music track title">
          <div className={styles.WsHeader__title}>
            <WsHeaderTitle />
          </div>
        </Tooltip>
        {path === Path.storyboard && (
          <>
            <WsHeaderMarkers ws={ws} />
            <WsHeaderMarkersReset />
            <div className={styles.WsHeader__right}>
              <WsButton
                className={cm(styles.WsHeader__ScrollLock, {
                  [styles['WsHeader__ScrollLock--active']]: scrollSyncLock,
                })}
                onClick={() => dispatch(setScrollSyncLock())}
                tip={`${scrollSyncLock ? 'Unlock' : 'Lock'} Storyboard tracks horizontal scroll sync`}
                tipPosition="left"
              >
                <MouseScroll />
              </WsButton>
              <WsButton
                onClick={toggleHeaderState}
                className={styles.WsHeader__expandIcon}
                tip={`${sbViewMode.audio === 'collapsed' ? 'Expand' : 'Reduce'} Music Track`}
                tipPosition="left"
              >
                {sbViewMode.audio === 'collapsed' ? <ExpandIcon /> : <CollapseIcon />}
              </WsButton>
            </div>
          </>
        )}
      </div>
    </Tooltip>
  );
};
